import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mic, Volume2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AudioSetup() {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [debugLog, setDebugLog] = useState([]);
  
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const intervalRef = useRef(null);

  // Add to debug log
  const log = (message) => {
    console.log(message);
    setDebugLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Get all audio input devices - using exact same approach as MultiCamStudio
  const getDevices = async () => {
    log('Getting audio devices...');
    
    try {
      // Request BOTH video and audio first - same as MultiCamStudio
      log('Requesting video + audio permission...');
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      log('Permission granted for video + audio');
      
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      log(`Total devices found: ${allDevices.length}`);
      
      const audioInputs = allDevices.filter(d => d.kind === 'audioinput');
      log(`Audio inputs found: ${audioInputs.length}`);
      
      // Log each device with full details
      audioInputs.forEach((d, i) => {
        log(`Device ${i + 1}: "${d.label || 'No label'}" (ID: ${d.deviceId.substring(0, 15)}...)`);
      });
      
      // Also log video and output devices for debugging
      const videoInputs = allDevices.filter(d => d.kind === 'videoinput');
      const audioOutputs = allDevices.filter(d => d.kind === 'audiooutput');
      log(`Video inputs: ${videoInputs.length}, Audio outputs: ${audioOutputs.length}`);
      
      setDevices(audioInputs);
      
      if (audioInputs.length === 0) {
        toast.warning('No audio devices found');
      } else {
        toast.success(`Found ${audioInputs.length} audio device(s)`);
      }
    } catch (err) {
      log(`Error: ${err.message}`);
      toast.error(err.message);
    }
  };

  // Request microphone permission first (required to see device labels)
  const requestPermission = async () => {
    log('Requesting microphone permission...');
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      log('Permission granted!');
      
      // Log what device we got
      const track = stream.getAudioTracks()[0];
      if (track) {
        log(`Got audio track: "${track.label}"`);
        const settings = track.getSettings();
        log(`Device ID: ${settings.deviceId?.substring(0, 15)}...`);
      }
      
      // Stop the stream
      stream.getTracks().forEach(t => t.stop());
      
      // Now get devices with labels
      await getDevices();
      
    } catch (err) {
      log(`Permission error: ${err.message}`);
      toast.error(err.message);
    }
  };

  // Listen to a specific device
  const listenToDevice = async (deviceId) => {
    // Stop any existing stream
    stopListening();
    
    log(`Listening to device: ${deviceId.substring(0, 15)}...`);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          deviceId: { exact: deviceId },
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        }
      });
      
      streamRef.current = stream;
      
      const track = stream.getAudioTracks()[0];
      log(`Now listening to: "${track.label}"`);
      
      // Set up audio level meter
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioCtxRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      
      const source = audioCtxRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      intervalRef.current = setInterval(() => {
        analyserRef.current.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(Math.min(100, avg * 2));
      }, 50);
      
      setIsListening(true);
      setSelectedDeviceId(deviceId);
      
    } catch (err) {
      log(`Listen error: ${err.message}`);
      toast.error(err.message);
    }
  };

  // Stop listening
  const stopListening = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    setIsListening(false);
    setAudioLevel(0);
  };

  // Save selected device
  const saveDevice = () => {
    const device = devices.find(d => d.deviceId === selectedDeviceId);
    if (device) {
      localStorage.setItem('selectedAudioDevice', JSON.stringify({
        deviceId: device.deviceId,
        label: device.label
      }));
      log(`Saved device: "${device.label}"`);
      toast.success('Device saved! Go to Mobile Studio to record.');
    }
  };

  // Cleanup
  useEffect(() => {
    return () => stopListening();
  }, []);

  // Initial device check
  useEffect(() => {
    getDevices();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/mobile-studio" className="p-2 bg-stone-800 rounded-lg">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-2xl font-bold">Audio Setup</h1>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3 mb-6">
        <button
          onClick={requestPermission}
          className="w-full py-4 bg-amber-600 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
        >
          <Mic className="w-6 h-6" />
          1. Request Permission
        </button>
        
        <button
          onClick={getDevices}
          className="w-full py-4 bg-blue-600 rounded-xl font-bold text-lg flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-6 h-6" />
          2. Refresh Device List
        </button>
      </div>

      {/* Device List */}
      <div className="bg-stone-900 rounded-xl p-4 mb-6">
        <h2 className="text-lg font-bold mb-3">Audio Devices ({devices.length})</h2>
        
        {devices.length === 0 ? (
          <p className="text-stone-500 text-center py-4">
            No devices found. Tap "Request Permission" first.
          </p>
        ) : (
          <div className="space-y-2">
            {devices.map((device, idx) => (
              <div
                key={device.deviceId}
                className={`p-3 rounded-lg border-2 ${
                  selectedDeviceId === device.deviceId 
                    ? 'border-green-500 bg-green-900/30' 
                    : 'border-stone-700 bg-stone-800'
                }`}
              >
                <p className="font-bold text-sm">
                  {device.label || `Device ${idx + 1} (no label)`}
                </p>
                <p className="text-xs text-stone-500 mb-2">
                  ID: {device.deviceId.substring(0, 20)}...
                </p>
                
                <button
                  onClick={() => listenToDevice(device.deviceId)}
                  className="w-full py-2 bg-purple-600 rounded-lg font-bold text-sm"
                >
                  🎧 Listen to this device
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audio Level Meter */}
      {isListening && (
        <div className="bg-stone-900 rounded-xl p-4 mb-6">
          <h2 className="text-lg font-bold mb-3">Audio Level</h2>
          <div className="flex items-center gap-3">
            <Volume2 className="w-6 h-6" />
            <div className="flex-1 h-8 bg-stone-800 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  audioLevel > 80 ? 'bg-red-500' : audioLevel > 50 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${audioLevel}%` }}
              />
            </div>
            <span className="text-xl font-bold w-16 text-right">{Math.round(audioLevel)}%</span>
          </div>
          <p className="text-center text-green-400 mt-2">Make noise to see the meter move!</p>
          
          <div className="flex gap-2 mt-4">
            <button
              onClick={stopListening}
              className="flex-1 py-3 bg-red-600 rounded-lg font-bold"
            >
              Stop
            </button>
            <button
              onClick={saveDevice}
              className="flex-1 py-3 bg-green-600 rounded-lg font-bold"
            >
              Save & Use This Device
            </button>
          </div>
        </div>
      )}

      {/* Go to Mobile Studio */}
      <Link
        to="/mobile-studio"
        className="block w-full py-4 bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl font-bold text-lg text-center mb-6"
      >
        Go to Mobile Studio →
      </Link>

      {/* Debug Log */}
      <div className="bg-stone-900 rounded-xl p-4">
        <h2 className="text-lg font-bold mb-2">Debug Log</h2>
        <div className="bg-black rounded-lg p-3 h-48 overflow-y-auto font-mono text-xs">
          {debugLog.length === 0 ? (
            <p className="text-stone-600">Logs will appear here...</p>
          ) : (
            debugLog.map((line, i) => (
              <p key={i} className="text-green-400">{line}</p>
            ))
          )}
        </div>
        <button
          onClick={() => setDebugLog([])}
          className="mt-2 px-3 py-1 bg-stone-700 rounded text-sm"
        >
          Clear Log
        </button>
      </div>
    </div>
  );
}
